import { useEffect, useState, useRef, Fragment } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search, FileText, Calendar, CreditCard, LineChart,
  ChevronRight, ArrowRight,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { apiUrl, fetchJson } from "@/lib/api";
import { gaTrack, metaTrack, metaTrackCustom, signupAudience } from "@/lib/analytics";
import { trackEvent } from "@/lib/tracker";
import useBottomBarClearance from "@/hooks/useBottomBarClearance";
import { loadCompanyDraft, clearCompanyDraft } from "@/lib/needDraft";
import { fetchWithAuth } from "@/utils/fetchWithAuth";
import { useBranding } from "../contexts/BrandingContext";
import SEO from "../components/SEO";
import SocialAuthButtons from "../components/SocialAuthButtons";
import { resolvePostLoginPath } from "../utils/postAuth";
import HeroTalentRail from "../components/HeroTalentRail";
import HeroFunnelModal from "../components/HeroFunnelModal";
import HeroNeedSearch from "../components/HeroNeedSearch";
import SiftScoreShowcase from "../components/SiftScoreShowcase";
import Reveal, { REVEAL_CSS } from "../components/Reveal";

/* ================================================================== */
/*  BUSINESS landing (primary audience). One audience, one message:    */
/*  streamline hiring + workforce operations. Structure follows VAL's   */
/*  feedback — hook (quantified) → problem → one-system statement →     */
/*  proven process → sign-up. No worker content lives here; that has    */
/*  its own page at /for-talent.                                        */
/* ================================================================== */

const CSS = REVEAL_CSS + `
@import url('https://fonts.googleapis.com/css2?family=Instrument+Sans:wght@400;500;600;700&family=Geist+Mono:wght@400;500&family=Caveat:wght@500;600;700&display=swap');
.bl { background:var(--bg); color:var(--ink); font-family:var(--font-sans); }
/* One width for the whole page. The hero, the flow, the sections and the
   carousel all measure the same, so nothing reads as an element that escaped
   its column. 1320 uses the screen on a laptop without letting the running
   text run past a comfortable measure, the paragraphs are capped separately
   by .bl-lede. */
.bl__wrap { max-width:1320px; margin:0 auto; padding:0 32px; }
@media (max-width:640px){ .bl__wrap { padding:0 20px; } }
.bl-mono { font-family:var(--font-mono); text-transform:uppercase; }
.bl-serif { font-family:var(--font-sans); letter-spacing:-.02em; }
.bl-em { font-style:italic; }
.bl-cta { display:inline-block; background:var(--brand); color:#fff; padding:15px 34px; border-radius:var(--r-pill); font-weight:600; font-size:1rem; box-shadow:var(--shadow-md); transition:background .2s; }
.bl-cta:hover { background:var(--brand-hover); }
/* Secondary door: larger orgs (hospitals, firms) won't self-onboard a payments
   platform, so every conversion point offers a walkthrough next to "start free". */
.bl-cta--ghost { display:inline-flex; align-items:center; gap:7px; background:transparent; color:var(--text-secondary); padding:15px 26px; border-radius:var(--r-pill); font-weight:600; font-size:0.96875rem; border:1px solid var(--border); box-shadow:none; transition:border-color .2s, color .2s; }
.bl-cta--ghost:hover { background:transparent; border-color:var(--brand); color:var(--brand); }
.bl-ctarow { display:flex; gap:12px; justify-content:center; align-items:center; flex-wrap:wrap; }
.bl-hero { position:relative; }
.bl-hns-wrap { display:block; }

/* The hero is one column at every width now. The second column held a single
   rotating card; that content runs full-bleed under the field instead, which
   uses the width the two-column version was splitting. */
.bl-hero-grid { display:block; }
.bl-hero-copy { text-align:left; }
/* Headline. Big enough to be the first thing read, and the number inside it is
   the argument: a live count is the one claim on the page a visitor can check
   by clicking. */
/* One line on a desktop. Sized in vw so it scales with the column rather than
   being pinned to a size that only fits at one width, and only held to a single
   line above 900px, below that it has to wrap or it would overflow. */
.bl-h1 { font-weight:700; font-size:clamp(2rem,7.4vw,2.75rem); line-height:1.04; margin:0;
  max-width:none; text-wrap:balance; }
@media (min-width:900px) {
  /* Sized up with the new headline, which is 33 characters against the old
     one's 47. Still in vw and still nowrap, so it scales with the column
     instead of being pinned to a size that only fits at one width. */
  .bl-h1 { white-space:nowrap; font-size:min(5vw, 70px); text-wrap:normal; }
}

/* The second half of the hook, and it does the work: the headline concedes,
   this names the five things. Wider measure and a size up from a body lede,
   because it is being read as part of the headline rather than after it. */
.bl-hero-lede { font-size:1.125rem; line-height:1.55; max-width:64ch; margin:18px 0 0;
  color:var(--text-secondary); }
@media (min-width:900px) { .bl-hero-lede { font-size:1.21875rem; max-width:68ch; } }

/* Price and the conversion fee, answered before the ask rather than three
   screens below it. Quiet on purpose: it removes an objection, it is not a
   sales line, and anything louder would compete with the field above it. */
.bl-hero-free { margin:14px 0 0; font-size:0.875rem; font-weight:500;
  color:var(--text-tertiary); letter-spacing:.005em; }

/* The quiet second door under the field. A link, not a button: the field is
   the ask and nothing beside it should compete for the same weight. */
/* .bl-browse is unused since "Browse talent" came out of the hero on
   2026-09-14. Kept, because it is the style for a quiet secondary door and the
   next thing tried in that slot will want it. */
.bl-browse { display:inline-flex; align-items:center; gap:7px; font-size:0.9375rem; font-weight:600;
  color:var(--text-secondary); text-decoration:none; border-bottom:1px solid transparent;
  padding-bottom:1px; transition:color .2s, border-color .2s; }
.bl-browse:hover { color:var(--brand); border-bottom-color:var(--brand); }

/* Frosted surfaces. The hero sits on two soft colour washes, and a solid white
   card punched over them reads as pasted on; letting the wash through ties the
   cards to the ground they sit on. The solid background is declared first so a
   browser without backdrop-filter still gets a readable card rather than a
   transparent one. */
.bl-glass { background:var(--surface); border:1px solid var(--border); }
@supports (backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)) {
  .bl-glass {
    background:color-mix(in srgb, var(--surface) 68%, transparent);
    backdrop-filter:saturate(150%) blur(14px);
    -webkit-backdrop-filter:saturate(150%) blur(14px);
    border-color:color-mix(in srgb, var(--border-strong) 55%, transparent);
    box-shadow:0 1px 0 rgba(255,255,255,.55) inset, var(--shadow-sm);
  }
}
/* Sections below the hero. Everything on this page used to be one centred
   column of 620px paragraphs, which is the shape of a printed document and the
   reason it read like one. A ranged-left measure with a second column beside it
   is the shape of a product page. The eyebrow gives each section a visible
   start, so the page scans as parts rather than as continuous prose. */
.bl-split { display:grid; grid-template-columns:1fr; gap:32px; align-items:center; text-align:left; }
@media (min-width:900px) { .bl-split { grid-template-columns:minmax(0,1.05fr) minmax(0,.95fr); gap:56px; } }
.bl-split--flip .bl-split__art { order:-1; }
@media (max-width:899px) { .bl-split--flip .bl-split__art { order:0; } }
.bl-split__art { display:flex; justify-content:flex-start; }
@media (min-width:900px) { .bl-split__art { justify-content:flex-end; } }

/* Big, red, and on its own. The eyebrow treatment was right when every section
   had one; a section whose whole job is to name a problem does not need a label
   above the label. */
.bl-shout { font-weight:700; letter-spacing:-.02em; line-height:1;
  font-size:clamp(1.875rem,4.4vw,3.25rem); color:#9E3B2F; margin:0 0 12px; }

/* Same eyebrow, without the rule. */
.bl-shout--brand { color:var(--brand); }
/* All three section headers share one size. Two of them at 84px turned the
   page into a shouting match with itself; the hierarchy is carried by colour
   and by order now, which is enough. */
.bl-eyebrow--bare::before { display:none; }

.bl-eyebrow { font-family:var(--font-mono); text-transform:uppercase; font-size:0.65625rem;
  letter-spacing:.18em; color:var(--brand); margin-bottom:12px; display:flex; align-items:center; gap:9px; }
.bl-eyebrow::before { content:""; width:22px; height:1px; background:var(--brand); flex:0 0 auto; }

/* The struck count. A drawn line rather than the default text-decoration, so
   its weight and colour hold at display sizes instead of thinning to a hair. */
.bl-strike { color:var(--text-muted); text-decoration:none; position:relative; white-space:nowrap; }
.bl-strike::after { content:""; position:absolute; left:-.04em; right:-.04em; top:53%;
  height:.075em; background:currentColor; border-radius:2px; transform:rotate(-1.6deg); }
.bl-just { color:var(--brand); text-transform:uppercase; letter-spacing:-.01em; white-space:nowrap; }

.bl-h2 { font-weight:700; font-size:clamp(1.625rem,3.1vw,2.375rem); line-height:1.1; margin:0;
  letter-spacing:-.02em; max-width:18ch; text-wrap:balance; }
.bl-lede { font-size:1.03125rem; line-height:1.6; color:var(--text-secondary); max-width:56ch; margin:16px 0 0; }

/* The six-step slog, as a numbered list. Numbering is load-bearing here: these
   genuinely happen in order, and the count is the point being made. */
.bl-chaos { list-style:none; margin:0; padding:0; width:100%; max-width:400px;
  display:flex; flex-direction:column; gap:2px; }
.bl-chaos li { display:flex; align-items:flex-start; gap:12px; padding:10px 14px;
  border-radius:var(--r-sm); }
.bl-chaos li:nth-child(odd) { background:var(--surface-sunken); }
.bl-chaos__n { flex:0 0 auto; width:20px; height:20px; border-radius:999px;
  background:var(--surface); border:1px solid var(--border-strong); color:var(--text-tertiary);
  font-family:var(--font-mono); font-size:0.65625rem; display:grid; place-items:center; margin-top:1px; }
.bl-chaos b { display:block; font-size:0.90625rem; font-weight:600; color:var(--ink); }
.bl-chaos i { display:block; font-style:normal; font-size:0.78125rem; color:var(--text-tertiary); margin-top:1px; }

.bl-hero-glow { position:absolute; inset:-30px 0 0; z-index:-1; pointer-events:none;
  background:radial-gradient(40% 55% at 25% 25%, var(--brand-tint), transparent 72%),
            radial-gradient(36% 48% at 82% 20%, var(--gold-soft), transparent 72%);
  opacity:.7; }


/* The five steps as a flow of nodes rather than a row of boxes. The cards put a
   border around each step and made the row read as five separate things; these
   read as one sequence, which is what it is. Numbering is load-bearing: they
   genuinely happen in this order. */
.bl-flow { list-style:none; display:flex; align-items:flex-start; gap:10px;
  margin:40px 0 8px; padding:0; }
.bl-node { flex:1 1 0; min-width:0; display:flex; flex-direction:column; align-items:center;
  text-align:center; gap:14px; }
.bl-node__dot { position:relative; width:58px; height:58px; border-radius:50%;
  background:var(--surface); border:2px solid var(--brand); color:var(--brand);
  display:grid; place-items:center; box-shadow:var(--shadow-sm); }
.bl-node__num { position:absolute; top:-7px; right:-7px; width:22px; height:22px;
  border-radius:50%; background:var(--brand); color:#fff; font-size:0.6875rem; font-weight:700;
  display:grid; place-items:center; border:2px solid var(--bg); }
.bl-node__title { font-weight:600; font-size:0.9375rem; color:var(--ink); line-height:1.3; }
/* Decoration, not content, so it carries aria-hidden and is the first thing
   dropped when the row stacks. */
.bl-flow__sep { flex:0 0 auto; display:flex; align-items:center; padding-top:18px;
  color:var(--brand); opacity:.45; }

@media (max-width:820px){
  .bl-flow { flex-direction:column; align-items:stretch; gap:18px; max-width:380px; }
  .bl-node { flex-direction:row; text-align:left; gap:16px; align-items:center; }
  .bl-flow__sep { display:none; }
}

/* Sticky closing CTA, pinned to the bottom of the viewport so the ask stays
   in reach the whole scroll. Sits above the page footer; page gets bottom
   padding so nothing hides behind it. */
.bl-stickybar { position:fixed; left:0; right:0; bottom:0; z-index:40;
  background:rgba(250,248,242,.9); backdrop-filter:saturate(180%) blur(12px); -webkit-backdrop-filter:saturate(180%) blur(12px);
  border-top:1px solid var(--border); padding:12px 24px calc(12px + env(safe-area-inset-bottom)); box-shadow:0 -8px 30px -18px rgba(27,24,19,.4); }
.bl-stickybar__wrap { max-width:1320px; margin:0 auto; display:flex; align-items:center; justify-content:space-between; gap:16px; }
.bl-stickybar__msg { font-weight:600; font-size:0.9375rem; }
.bl-stickybar__sub { font-weight:400; font-size:0.8125rem; color:var(--text-secondary); }
.bl-stickybar__cta { flex:0 0 auto; display:inline-flex; align-items:center; gap:8px; background:var(--brand); color:#fff; padding:13px 30px; border-radius:var(--r-pill); font-weight:700; font-size:0.96875rem; box-shadow:var(--shadow-md); transition:background .2s; white-space:nowrap; }
.bl-stickybar__cta:hover { background:var(--brand-hover); }
.bl-stickybar__actions { flex:0 0 auto; display:flex; align-items:center; gap:18px; }
.bl-stickybar__alt { font-weight:600; font-size:0.90625rem; color:var(--text-secondary); text-decoration:underline; text-underline-offset:3px; white-space:nowrap; }
.bl-stickybar__alt:hover { color:var(--ink); }
@media (max-width:560px){
  .bl-stickybar__msg { font-size:0.875rem; }
  .bl-stickybar__sub { display:none; }
  .bl-stickybar__cta { padding:12px 22px; font-size:0.90625rem; }
  .bl-stickybar__actions { gap:12px; flex-direction:column; align-items:stretch; }
  .bl-stickybar__alt { font-size:0.84375rem; text-align:center; }
}
`;

// Two doors at every conversion point. Self-serve is primary; the walkthrough
// catches orgs (hospitals, firms) that won't self-onboard a payments platform.
// It lands on the contact form in walkthrough mode, not a third-party booker,
// so the ask is real today rather than a link to nowhere.
const WALKTHROUGH_PATH = "/walkthrough";

// The Sift Method as a flow map. Titles only: the captions under each one were
// restating the title in more words, and five of them turned a five-beat row
// into a paragraph laid out sideways. [Icon, title].
const STEPS = [
  [Search, "Post what you need"],
  [FileText, "Agree on terms"],
  [Calendar, "Schedule the work"],
  [CreditCard, "Approve and pay"],
  [LineChart, "See your numbers"],
];


/* The five-step flow, extracted so it can sit directly under the hero. The
   #how-it-works id travels with it — other pages and the nav still link here.

   Was a centred row of numbered circles joined by chevrons, which is the
   diagram every B2B template ships with. It is now a row of cards ranged left
   with the rest of the page. The numbering stays because these genuinely happen
   in this order: it is a sequence, not five features in a row. */
function SiftMethod() {
  return (
    <section id="how-it-works" className="bl__wrap" style={{ padding: "56px 24px 8px", scrollMarginTop: 80 }}>
      <div className="bl-eyebrow">How it works</div>
      <h2 className="bl-serif bl-h2" style={{ maxWidth: "24ch" }}>
        {/* The old count struck out and the new one beside it: the claim is a
            reduction, so the headline shows the reduction happening rather than
            describing it. The <s> is real markup, not a border, so a screen
            reader announces it as deleted text. */}
        <s className="bl-strike">A dozen steps</s>{" "}
        <span className="bl-just">Just five.</span>
      </h2>

      <ol className="bl-flow">
        {STEPS.map(([Icon, title], i) => (
          <Fragment key={title}>
            {i > 0 && <li className="bl-flow__sep" aria-hidden="true"><ChevronRight size={22} strokeWidth={2.4} /></li>}
            <Reveal as="li" i={i} className="bl-node">
              <span className="bl-node__dot" aria-hidden="true">
                <Icon size={24} strokeWidth={1.7} />
                <span className="bl-node__num">{i + 1}</span>
              </span>
              <span className="bl-node__title">{title}</span>
            </Reveal>
          </Fragment>
        ))}
      </ol>

    </section>
  );
}

export default function BusinessLanding({ adLP = false }) {
  const branding = useBranding();
  const [foundingLeft, setFoundingLeft] = useState(null);

  const [availabilityEmpty, setAvailabilityEmpty] = useState(false);
  /* The company funnel, opened over this page from the hero field. Only the
     hero uses it; "Get started" still goes to /get-started as its own page. */
  const [funnelOpen, setFunnelOpen] = useState(false);
  /* What the hero asks for. Assigned once at mount for the same reason the
     other hero test is: an arm that swaps after paint is a visible flicker and
     an impression recorded against something nobody read. */

  // What the arm actually rendered, which is not always what it was assigned:
  // the availability arm falls back to the video on an empty board. Every hero
  // event carries this so a fallback is never counted as a win for either side.
  const heroShown = availabilityEmpty ? "none" : "availability";

  useEffect(() => {
    trackEvent("hero_variant_shown", {
      shown: heroShown,
      account_type: "organization",
    });
  }, [heroShown]);

  /* trackHeroCta is gone with the "Browse talent" link, its only caller. The
     hero has one action now and it reports itself: HeroNeedSearch fires
     hero_need_submitted, which is the number that matters here. */

  /* The sticky CTA stays out of the way until the hero is behind them.

     It used to key off the Sift Method scrolling into view, which reads as
     "once the page has explained itself" and behaved as "immediately": that
     section sits directly under the hero, so on most laptops it is already 15%
     on screen at load and the bar appeared before anyone had scrolled at all.

     Now it keys off the hero leaving the top of the viewport, which is the
     thing actually being asked for. The `top < 0` test is what separates that
     from the hero not having been reached yet, since both are "not
     intersecting". Latches on, because a CTA that comes and goes while you
     scroll is worse than one that is always there. */
  const heroRef = useRef(null);
  /* Up from the first frame, owner's call 2026-09-18. It previously waited for
     the hero to clear the top of the viewport, which on a phone meant most
     visitors never saw it: they leave from the hero. */
  const [showSticky] = useState(true);
  const stickyRef = useRef(null);
  useBottomBarClearance(stickyRef, showSticky);


  useEffect(() => {
    let alive = true;
    fetchJson(apiUrl("/api/accounts/public/founding-stats/"))
      .then((d) => { if (alive) setFoundingLeft(d?.orgs_left ?? null); })
      .catch(() => { if (alive) setFoundingLeft(null); });
    return () => { alive = false; };
  }, []);

  const navigate = useNavigate();

  /* One tap here makes the ACCOUNT. The company is asked for next.

     This used to make an account and stop, which is how a real visitor on
     14 September pressed "Sign up with Google" at the bottom of the company
     page and ended up a jobseeker: OAuth hands back a person, never a company,
     and nothing downstream knew to ask. In the three days around it the
     platform created 108 users and zero organisations.

     Two things make the tap safe now. SocialAuthButtons sends the audience, so
     the server builds the account company-shaped (is_org_owner, and no
     service profile). And resolvePostLoginPath sends an org owner who has no
     organisation yet to /company/onboarding, which is where the name, the
     website and the rest get collected -- rather than to the contractor
     dashboard, which is the wrong room entirely. */
  const onSocialAuthed = async (data, provider) => {
    // account_created / sign_up / CompleteRegistration come from
    // SocialAuthButtons, the only place that knows which provider was used.
    if (data?.is_new_user) {
      trackEvent("verification_completed", { method: "oauth", provider, account_type: "organization" });
      metaTrack("StartTrial", signupAudience("organization"));
    }
    /* Straight into the get-started flow, not to a bare form.

       The account exists now, but an account is not the thing we want from
       them: we want to know what they are hiring for. So they get the same
       walk everybody else gets -- their website read, a posting drafted, the
       people who match it shown -- and the company is named at the end of it.
       Everything they touch on the way is recorded against their email in the
       sourcing queue, so a company that stops halfway is a lead with a name
       on it rather than a row nobody can act on.

       A returning user with a company already goes to their dashboard, which
       is what resolvePostLoginPath answers for them. */
    /* Publish what they already wrote, now, while we have both halves.
       They drafted a post before signing up, and the account has an
       organisation from the moment it is created, so there is nothing left to
       wait for. Leaving it until a later screen is how the drafts were being
       lost: they close the tab and the post they wrote never existed. */
    const orgId = data?.user?.organization?.id;
    const pending = loadCompanyDraft();
    if (data?.is_new_user && orgId && pending?.title) {
      try {
        await fetchWithAuth("/api/accounts/postings/", {
          method: "POST",
          json: {
            poster_type: "org",
            organization_id: String(orgId),
            title: String(pending.title).slice(0, 255),
            description: pending.description || "",
            location: pending.location || "",
          },
        });
        clearCompanyDraft();
        metaTrackCustom("JobPosted", signupAudience("organization"));
        gaTrack("job_posting_created", { poster_type: "org" });
        trackEvent("first_posting_created", {
          poster_type: "org", account_type: "organization", flow: "oauth_register",
        });
        toast.success("You're in, and your post is live.");
      } catch {
        trackEvent("posting_create_failed", {
          account_type: "organization", flow: "oauth_register",
        });
        toast.success("You're in. Your draft is saved, give it one last look.");
      }
    } else {
      toast.success("You're in. Let's see who's available for you.");
    }
    let dest = "/get-started?a=business&c=1&door=post";
    try {
      const resolved = await resolvePostLoginPath(branding, data?.user);
      // Only honour it when it points somewhere real: for a brand-new company
      // it answers /auth/organization/register, which is the bare form this
      // is deliberately replacing.
      if (resolved && !resolved.includes("organization/register")) dest = resolved;
    } catch { /* the flow is the right default */ }
    navigate(dest);
  };

  return (
    <div className="bl">
      <SEO
        title="A Trusted Services Marketplace"
        description="Sift Careers is the one platform to hire, schedule, verify, and pay your workforce. Contracts, timecards, and payment in one place, with no agency markup and no conversion fees."
        path="/"
        keywords={["workforce management platform", "hiring operations software", "contractor management", "credential tracking", "timecard and payment platform", "staffing operations", "workforce coordination"]}
        noIndex={adLP}
      />
      <style>{CSS}</style>

      <HeroFunnelModal open={funnelOpen} onClose={() => setFunnelOpen(false)} />

      {/* ============================= HERO ============================= */}
      <section ref={heroRef} className="bl__wrap bl-hero" style={{ paddingTop: 40, paddingBottom: 0 }}>
        <div className="bl-hero-glow" aria-hidden="true" />
        <div className="bl-hero-grid">
        <div className="bl-hero-copy">
        {/* The hook concedes the thing every competitor sells, in the first
            sentence, which is what earns the second one.

            It replaces "Post your needs, or browse available talent now.",
            which described the interface rather than saying anything: two
            verbs, both of them instructions, neither of them a reason. Over
            the fortnight to 2026-09-09 it was read by 606 company visitors,
            33 of whom started anything and one of whom finished. A hero that
            asks somebody to work before it has said why is the failure that
            number describes.

            This is the line already carrying the App Store listing, and it is
            the one thing on the page written from the reader's side of the
            screen: it names their week back to them. The list is specific
            enough that somebody living it recognises it, which is the whole
            job of a hook. */}
        <h1 className="bl-serif bl-h1">
          Finding someone is the easy part.
        </h1>
        <p className="bl-lede bl-hero-lede">
          It's the credentials, the contract, the schedule, the timecards and the
          payment that eat your week. Sift is all five in one place.
        </p>
        <div style={{ marginTop: 24 }} className="bl-hns-wrap">
          <HeroNeedSearch onOpen={() => setFunnelOpen(true)} />
        </div>
        {/* The only two things a company wants to know before typing into a box
            on a site it has never used: what this costs, and whether liking
            somebody is going to be expensive. Both answered before the ask, in
            one line, because they are the objections that stop a first move.

            "Browse talent" used to sit here and is gone as of 2026-09-14. It
            pointed off the flow into a catalogue, which is the one thing a
            visitor can do on this page that cannot produce an account. */}
        <p className="bl-hero-free">
          Free to start. No conversion fee, ever.
        </p>
        </div>
        </div>

        {/* The board, run across the full width rather than parked in a column
            beside the copy. It stays now that the headline has stopped offering
            to browse, and for a better reason than the headline: it is the only
            evidence on the page that anybody is actually here. A claim about
            what Sift does, with a row of real people under it, is a different
            page from the same claim on its own.

            It goes once the visitor has searched. At that point their own
            matches are the proof, and a generic rail underneath them is noise.

            The video that used to sit here is gone: 135 people were shown it,
            four pressed play, and none of those four clicked a company CTA. */}
        {availabilityEmpty ? null : (
          <div style={{ marginTop: 34 }}>
            <HeroTalentRail onEmpty={() => setAvailabilityEmpty(true)} />
          </div>
        )}
      </section>

      {/* ================= THE SIFT METHOD (moved above the problem) =================
          VAL's read, and the funnel agrees: the old order made a visitor scroll
          through an animated chaos graphic before learning what the site
          actually does. Leading with the five-step flow states the product in
          one glance, and puts the CTA in the first screenful. */}
      {/* How it works, parked at Vinnie's request 2026-09-09. Commented out
          rather than deleted: the component, its STEPS and its CSS are all
          still here, so putting it back is uncommenting this one line. */}
      {/* <SiftMethod /> */}
      {/* ============================= PROBLEM ============================= */}
      <section id="the-problem" className="bl__wrap bl-split" style={{ padding: "44px 24px 8px", scrollMarginTop: 80 }}>
        <div className="bl-split__copy">
          <div className="bl-shout">Problem</div>
          <h2 className="bl-serif bl-h2">Everything hinges on you.</h2>
          <p className="bl-lede">
            Finding someone is the easy part. Then comes verifying they're qualified, agreeing on
            terms, getting a contract signed, scheduling them, chasing timecards, and paying them.
            Six disconnected tools and a hundred emails.
          </p>
          <p className="bl-lede">
            Managing your workforce shouldn't feel like a second full-time job. It's time you
            don't get to spend on the work that matters.
          </p>
        </div>
        {/* The six steps as a list rather than a sentence: the count is the
            argument, and a reader who skims should still land on "six". */}
        <div className="bl-split__art">
          <ul className="bl-chaos" aria-label="What one hire currently takes">
            {[
              ["Verify", "licenses, insurance, references"],
              ["Agree terms", "rate, scope, dates"],
              ["Get it signed", "contract chased over email"],
              ["Schedule", "a spreadsheet somebody else edits"],
              ["Chase timecards", "every single week"],
              ["Pay", "invoices, then reconcile"],
            ].map(([t, c], i) => (
              <Reveal as="li" i={i} key={t}><span className="bl-chaos__n">{i + 1}</span><span><b>{t}</b><i>{c}</i></span></Reveal>
            ))}
          </ul>
        </div>
      </section>
      {/* ===================== TRUST — the Sift Score =====================
          The Sift Score was one bolded phrase inside a paragraph. It is the
          answer to the first objection anybody has about a marketplace, so it
          gets a section and a picture of itself. Two columns, ranged left: the
          centred single column this replaced was the thing that made the page
          read like a document rather than a product. */}
      <section className="bl__wrap bl-split" style={{ padding: "56px 24px 8px" }}>
        <div className="bl-split__copy">
          <h2 className="bl-serif bl-shout bl-shout--brand">Sift Score</h2>
          <p className="bl-serif bl-h2" style={{ margin: "0 0 4px" }}>Verified people, not usernames.</p>
          <p className="bl-lede">
            Everyone on Sift carries a Sift Score: one number built from verified credentials,
            completed contracts and ratings from real engagements. You see who someone is before
            you ever message them.
          </p>
          <p className="bl-lede">
            It is earned, and it can be lost. A single one-star review costs twenty points and
            there is no cap on the penalty, so the score cannot be padded into meaninglessness.
            No anonymous bids, no race to the bottom on price and quality.
          </p>
        </div>
        <div className="bl-split__art">
          <SiftScoreShowcase />
        </div>
      </section>
      {/* ============== DIFFERENTIATOR — no conversion fee ==============
          The sharpest card against agencies. Ranged left with the rest. */}
      <section className="bl__wrap" style={{ padding: "56px 24px 8px" }}>
        <h2 className="bl-serif bl-shout bl-shout--brand" style={{ maxWidth: "20ch" }}>No conversion fee</h2>
        <p className="bl-lede" style={{ marginTop: 10 }}>Found someone great, keep them.</p>
      </section>

      {/* ============================= CTA BAND ============================= */}
      <section className="bl__wrap" style={{ padding: "40px 24px 60px" }}>
        <div style={{ position: "relative", overflow: "hidden", background: "var(--ink)", color: "var(--on-dark)", borderRadius: "var(--r-2xl)", padding: "48px 32px", textAlign: "center" }}>
          <div aria-hidden="true" style={{ position: "absolute", top: "-40%", left: "50%", transform: "translateX(-50%)", width: 520, height: 520, background: "radial-gradient(50% 50% at 50% 50%, rgba(224,201,136,.18), rgba(224,201,136,0) 70%)", pointerEvents: "none" }} />
          <div style={{ position: "relative" }}>
            {foundingLeft > 0 && (
              <div style={{ marginBottom: 16 }}>
                <span className="bl-mono" style={{ display: "inline-block", fontSize: "0.6875rem", letterSpacing: ".12em", fontWeight: 700, color: "#1B1813", background: "var(--gold-soft)", borderRadius: "var(--r-pill)", padding: "6px 14px" }}>
                  Only {foundingLeft} of 100 founding seats left. Waived subscription fees for life.
                </span>
              </div>
            )}
            <h2 className="bl-serif" style={{ fontWeight: 700, fontSize: "clamp(1.625rem,3vw,2.25rem)", margin: 0, lineHeight: 1.08 }}>
              Put your whole workforce in one place.
            </h2>
            <p style={{ color: "var(--on-dark-muted)", maxWidth: 460, margin: "14px auto 24px", fontSize: "1rem" }}>
              Free to join. Set up in minutes.
            </p>
            <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", alignItems: "center" }}>
              <Link to="/get-started?a=business&c=1" data-analytics="cta_start_free:biz_cta_band" onClick={() => gaTrack("cta_get_started", { location: "biz_cta_band", account_type: "organization" })} style={{ background: "var(--brand)", color: "#fff", padding: "14px 32px", borderRadius: "var(--r-pill)", fontWeight: 600, whiteSpace: "nowrap" }}>
                Start free
              </Link>
              {/* "See who's available" and "Book a walkthrough" removed
                  2026-09-14. Three actions in a row and two of them lead away
                  from signing up: the catalogue is the one thing on this page
                  a visitor can do that cannot produce an account, and the
                  walkthrough parks them in a calendar. The closing band asks
                  once now: start, or one tap. */}
            </div>

            {/* One tap makes the account; the company is the next question.

                Facebook does not render here: SocialAuthButtons hides it on
                the company side, because it is not a work login and it was the
                button a company pressed on its way to a jobseeker account.
                Google and Microsoft are what a company recognises. */}
            <div style={{ maxWidth: 340, margin: "22px auto 0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                <span style={{ flex: 1, height: 1, background: "rgba(255,255,255,.18)" }} />
                <span className="bl-mono" style={{ fontSize: "0.625rem", letterSpacing: ".16em", color: "var(--on-dark-muted)" }}>or sign up instantly</span>
                <span style={{ flex: 1, height: 1, background: "rgba(255,255,255,.18)" }} />
              </div>
              {/* "your company" in the label, and a way out for a worker. This
                  tap always makes a company account, and in the 90 days to
                  2026-10-06 two of the five people who used it went straight
                  to their personal side and posted availability (Karl Carlson,
                  Shannon Chan): workers filed as companies. */}
              <SocialAuthButtons verb="Sign up your company" signup onAuthed={onSocialAuthed} accountType="organization" />
              <p style={{ marginTop: 14, textAlign: "center", fontSize: "0.875rem", color: "var(--on-dark-muted)" }}>
                Looking for work?{" "}
                <Link
                  to="/get-started?a=talent"
                  data-analytics="looking_for_work:biz_cta_band"
                  style={{ color: "#fff", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3 }}
                >
                  Start here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spacer so the closing content clears the fixed sticky bar, and only
          while there is one to clear. */}
      {showSticky && <div aria-hidden="true" style={{ height: 84 }} />}

      {/* ===================== STICKY CTA FOOTER =====================
          Held back until the hero has scrolled off the top of the screen.

          Asking for the account is a different question from "is anyone here",
          and the second one has to be answered first: 72% of company visitors
          in the week to 08-19 reached the signup form and touched nothing at
          all. A bar that pins itself to the screen from the first pixel is
          asking before it has said anything worth saying, and it costs the
          bottom 84px of a phone screen for the whole visit to do it.

          Rendered, not just hidden, so nothing occupies the viewport before
          its moment; once shown it stays, because a CTA that comes and goes as
          you scroll is worse than either. */}
      {showSticky && (
      <div ref={stickyRef} className="bl-stickybar" role="complementary" aria-label="Get started with Sift">
        <div className="bl-stickybar__wrap">
          <div>
            <div className="bl-stickybar__msg">Ready to stop being the coordination layer?</div>
            <div className="bl-stickybar__sub">Free to join. Set up in minutes.</div>
          </div>
          {/* One button (Jake, 2026-10-04). Signup opens on "What can we help
              you with?", which sorts posting, signing up and finding work from
              their own words, so the bar no longer has to offer two doors. */}
          <div className="bl-stickybar__actions">
            <Link
              to="/get-started?a=business&c=1"
              className="bl-stickybar__cta"
              data-analytics="cta_get_started:biz_sticky_bar"
              onClick={() => gaTrack("cta_get_started", { location: "biz_sticky_bar", account_type: "organization" })}
            >
              Get started <ArrowRight size={18} strokeWidth={2.4} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
